import React, { useEffect } from 'react';
import { useCart } from '../context/CartContext';

// Simple SVG Icon for Trash Can
const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12.56 0c1.153 0 2.24.03 3.22.076M6.166 20.973A2.25 2.25 0 014.044 19.673L2.955 5.79A2.25 2.25 0 015.2 3.72h13.6c2.244 0 4.007 1.617 4.166 3.62l-1.016 14.083a2.25 2.25 0 01-2.244 2.077H6.166z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const Cart = ({ isOpen, onClose }) => {
  const { cartItems, removeItemFromCart, updateItemQuantity, getCartTotal, generateShopifyCheckoutUrl, clearCart } = useCart();

  useEffect(() => {
    // Prevent body scroll when cart is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    // Cleanup function to reset body scroll when component unmounts or isOpen changes
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleIndividualQuantityChange = (variantId, amount) => {
    const item = cartItems.find(i => i.variantId === variantId);
    if (item) {
      const newQuantity = item.quantity + amount;
      updateItemQuantity(variantId, newQuantity); // updateItemQuantity handles if newQuantity <= 0
    }
  };

  const handleCheckout = () => {
    const checkoutUrl = generateShopifyCheckoutUrl();
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    } else {
      alert("Your cart is empty.");
    }
    onClose(); // Close cart after attempting checkout
  };

  return (
    <div 
      className={`fixed inset-0 z-50 flex justify-end transition-opacity duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
    >
      {/* Backdrop */} 
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Cart Drawer */} 
      <aside 
        className={`relative w-full max-w-sm h-full bg-white shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */} 
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Your Cart</h2>
          <button 
            onClick={onClose} 
            className="p-2 text-gray-500 hover:text-gray-800 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close cart"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Cart Items */} 
        {cartItems.length === 0 ? (
          <div className="flex-grow flex flex-col items-center justify-center p-6 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 text-gray-400 mb-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
            </svg>
            <p className="text-gray-600 text-lg">Your cart is empty.</p>
            <p className="text-sm text-gray-500 mt-1">Looks like you haven't added anything yet.</p>
            <button 
                onClick={onClose} 
                className="mt-6 bg-gray-800 text-white py-2 px-6 rounded-lg hover:bg-gray-700 transition-colors text-sm font-semibold"
            >
                Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="flex-grow overflow-y-auto p-4 sm:p-6 space-y-4">
              {cartItems.map((item) => (
                <div key={item.variantId} className="flex items-center space-x-3 p-3 bg-gray-50/70 rounded-lg shadow-sm border border-gray-200">
                  <img src={item.image} alt={item.name} className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-md border border-gray-200" />
                  <div className="flex-grow">
                    <h3 className="text-sm sm:text-base font-medium text-gray-800">{item.name}</h3>
                    {item.size && <p className="text-xs sm:text-sm text-gray-500">Size: {item.size}</p>}
                    <p className="text-sm text-gray-700 font-semibold mt-0.5">${item.price.toFixed(2)}</p>
                    {/* Quantity +/- Buttons for Cart Item */}
                    <div className="flex items-center mt-2 border border-gray-300 rounded-md w-min">
                        <button 
                            onClick={() => handleIndividualQuantityChange(item.variantId, -1)} 
                            className="px-2 py-0.5 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-l-md disabled:opacity-50 transition-colors"
                            disabled={item.quantity <= 1}
                            aria-label="Decrease quantity"
                        >
                            -
                        </button>
                        <span className="w-8 text-center text-xs p-0.5 border-l border-r border-gray-300 text-gray-800 bg-white">
                            {item.quantity}
                        </span>
                        <button 
                            onClick={() => handleIndividualQuantityChange(item.variantId, 1)} 
                            className="px-2 py-0.5 text-sm font-medium text-gray-600 hover:bg-gray-200 rounded-r-md transition-colors"
                            aria-label="Increase quantity"
                        >
                            +
                        </button>
                    </div>
                  </div>
                  <button onClick={() => removeItemFromCart(item.variantId)} className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100/50 transition-colors self-start mt-1">
                      <TrashIcon />
                  </button>
                </div>
              ))}
            </div>
            {/* Footer / Checkout Area */} 
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50/70">
              <div className="flex justify-between items-center mb-1">
                  <p className="text-xs text-gray-600">Subtotal</p>
                  <p className="text-xs text-gray-800">${getCartTotal().toFixed(2)}</p>
              </div>
              <div className="flex justify-between items-center mb-3">
                <p className="text-lg font-semibold text-gray-800">Total:</p>
                <p className="text-lg font-bold text-gray-900">${getCartTotal().toFixed(2)}</p>
              </div>
              <button 
                onClick={handleCheckout}
                className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-3 px-4 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 transition-colors duration-150"
              >
                Proceed to Checkout
              </button>
              {/* Shipping Note START */} 
              <p className="text-xs text-gray-500 text-center mt-3">
                Orders will ship in ~10 days.
              </p>
              {/* Shipping Note END */} 
              {cartItems.length > 0 && (
                <button 
                    onClick={() => { clearCart(); onClose(); }}
                    className="w-full text-center text-xs text-gray-500 hover:text-red-600 hover:underline font-medium mt-2"
                >
                    Clear Cart & Close
                </button>
              )}
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default Cart; 